<script setup lang="ts">
import { computed, type DeepReadonly, inject } from 'vue'

import {
  FILTER_TARGET_CONFIG,
  getActiveSocialMetric,
  getFilterTargetMetricAmount,
  getFilterTargets,
} from '../../adapters/campaign-performance-filter.adapter'
import { formatterProviderKey } from '../../constants'
import { defaultFormatter, formatNumber } from '../../formatters'
import {
  type CampaignChartPoint,
  type CampaignMetrics,
  type CampaignMetricTotals,
  CampaignOverviewFilterTargetEnum,
  type CampaignOverviewViewMode,
  type CampaignSourceMetrics,
  type CampaignStats,
} from '../../types/campaign.types'

import CampaignPerformanceChart from './CampaignPerformanceChart.vue'

const props = defineProps<{
  currency: string
  campaignStats?: CampaignStats
  isCampaignStatsLoading: boolean
  socialMetricTotals?: CampaignMetricTotals
  socialChartPoints?: DeepReadonly<CampaignChartPoint[]>
  isSocialChartLoading: boolean
  isStatsChartLoading?: boolean
  viewMode: CampaignOverviewViewMode
  maxDateRangeDays?: number
}>()
const { formatMoney } = inject(formatterProviderKey, defaultFormatter)
const dateRange = defineModel<{ startDate: string; endDate: string }>('dateRange')
const activeFilterTarget = defineModel<CampaignOverviewFilterTargetEnum>('activeFilterTarget', {
  default: CampaignOverviewFilterTargetEnum.Clicks,
})

const TRAFFIC_SOURCES: Record<number, { platform: string; label: string }> = {
  0: { platform: 'other', label: 'Other' },
  1: { platform: 'instagram', label: 'Instagram' },
  2: { platform: 'tiktok', label: 'TikTok' },
  3: { platform: 'snapchat', label: 'Snapchat' },
}
const breakdownLabels: Record<string, string> = {
  all: 'All',
  instagram: 'Instagram',
  tiktok: 'TikTok',
  snapchat: 'Snapchat',
  other: 'Other',
}

const activeSocialMetric = computed(() => getActiveSocialMetric(activeFilterTarget.value))

// 'compact' powers the button counts and chart axis/tooltip; 'standard' the full value.
const formatAmount = (amount = 0, target: CampaignOverviewFilterTargetEnum, notation: 'compact' | 'standard') => {
  if (FILTER_TARGET_CONFIG[target].format === 'currency') {
    return formatMoney(
      { amount, currency: props.currency },
      { notation, minimumFractionDigits: 0, maximumFractionDigits: 2, allowZero: true },
    )
  }
  return notation === 'compact' ? formatNumber(amount, 'compact') : formatNumber(amount)
}
const getCompactFormat = (amount = 0, target: CampaignOverviewFilterTargetEnum) => formatAmount(amount, target, 'compact')
const getStyledFormat = (amount = 0, target: CampaignOverviewFilterTargetEnum) => formatAmount(amount, target, 'standard')
const getValueUnit = (target: CampaignOverviewFilterTargetEnum): string => FILTER_TARGET_CONFIG[target].unit ?? ''

const getMetricAmount = (target: CampaignOverviewFilterTargetEnum, metrics?: CampaignMetrics | CampaignSourceMetrics): number =>
  getFilterTargetMetricAmount(target, props.socialMetricTotals, metrics)

type FilterTargetValues = {
  value: CampaignOverviewFilterTargetEnum
  label: string
  amount: number
  shortAmount: string
  longAmount: string
}
const getTargetValues = (
  target: CampaignOverviewFilterTargetEnum,
  metrics?: CampaignMetrics | CampaignSourceMetrics,
): FilterTargetValues => {
  const amount = getMetricAmount(target, metrics)
  return {
    value: target,
    label: FILTER_TARGET_CONFIG[target].label,
    amount,
    shortAmount: getCompactFormat(amount, target),
    longAmount: getStyledFormat(amount, target),
  }
}

const buttons = computed(() => getFilterTargets(props.viewMode).map((target) => getTargetValues(target, props.campaignStats?.summary)))

const getSourcesBreakdown = (sources: CampaignSourceMetrics[]) =>
  sources
    .map((source) => {
      const { platform, label } = TRAFFIC_SOURCES[source.source] ?? { platform: 'other', label: 'Other' }
      const amount = getMetricAmount(activeFilterTarget.value, source)
      return {
        platform,
        label,
        amount,
        formatted: getCompactFormat(amount, activeFilterTarget.value),
      }
    })
    .sort((a, b) => b.amount - a.amount)

const computedData = computed(() => {
  const target = activeFilterTarget.value
  if (activeSocialMetric.value) {
    return (props.socialChartPoints ?? []).map((point) => ({
      x: point.date,
      y: point.value,
      longAmount: getStyledFormat(point.value, target),
      valueUnit: getValueUnit(target),
      breakdown: [...point.breakdown]
        .sort((a, b) => b.value - a.value)
        .map((entry) => ({
          platform: entry.key,
          label: breakdownLabels[entry.key] ?? entry.key,
          formatted: getCompactFormat(entry.value, target),
        })),
    }))
  }
  return (
    props.campaignStats?.dailyStats.map((item) => {
      const values = getTargetValues(target, item)
      return {
        x: item.startDate,
        y: values.amount,
        longAmount: values.longAmount,
        valueUnit: getValueUnit(target),
        breakdown: getSourcesBreakdown(item.sources),
      }
    }) || []
  )
})

const handleFilterClick = (filterTarget: string) => (activeFilterTarget.value = filterTarget as CampaignOverviewFilterTargetEnum)
const formatYAxisLabel = (value: number) => getCompactFormat(value, activeFilterTarget.value)
</script>
<template>
  <CampaignPerformanceChart
    v-model:date-range="dateRange"
    :buttons="buttons"
    :active-filter-target="activeFilterTarget"
    :is-loading="isCampaignStatsLoading || !campaignStats"
    :is-chart-loading="activeSocialMetric ? isSocialChartLoading : !!isStatsChartLoading"
    :chart-data="computedData"
    :format-y-axis-label="formatYAxisLabel"
    :max-date-range-days="maxDateRangeDays"
    @filter-click="handleFilterClick"
  />
</template>
